/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React from 'react';
import '../styles/EnhancedSkeletonLoader.css';

/**
 * Association Dashboard Skeleton Loader
 */
export const AssociationDashboardSkeleton = () => (
  <div className="dashboard-skeleton" style={{ padding: '2rem' }}>
    {/* Stats Grid Skeleton */}
    <div className="skeleton-stats-grid" style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem'
    }}>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="skeleton-stat-card shimmer" style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #e5e7eb'
        }}>
          <div className="skeleton-icon" style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: '#e5e7eb',
            marginBottom: '1rem'
          }}></div>
          <div className="skeleton-stat-content">
            <div className="skeleton-text shimmer" style={{ width: '60px', height: '32px', marginBottom: '0.5rem', background: '#e5e7eb', borderRadius: '4px' }}></div>
            <div className="skeleton-text shimmer" style={{ width: '100px', height: '16px', background: '#e5e7eb', borderRadius: '4px' }}></div>
          </div>
        </div>
      ))}
    </div>
    
    {/* Content Cards Skeleton */}
    <div className="skeleton-cards-grid" style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1.5rem'
    }}>
      {[1, 2].map((i) => (
        <div key={i} className="skeleton-card shimmer" style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #e5e7eb'
        }}>
          <div className="skeleton-card-header" style={{ marginBottom: '1rem' }}>
            <div className="skeleton-text shimmer" style={{ width: '150px', height: '24px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '0.5rem' }}></div>
            <div className="skeleton-text shimmer" style={{ width: '80px', height: '20px', background: '#e5e7eb', borderRadius: '4px' }}></div>
          </div>
          <div className="skeleton-card-body">
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="skeleton-text shimmer" style={{ 
                width: j === 4 ? '70%' : '100%', 
                height: '16px', 
                marginBottom: '0.75rem',
                background: '#e5e7eb',
                borderRadius: '4px'
              }}></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Association Members Table Skeleton
 */
export const AssociationMembersSkeleton = ({ rows = 5 }) => (
  <div className="skeleton-table" style={{ padding: '1.5rem' }}>
    {/* Search and Filters Skeleton */}
    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
      <div className="skeleton-text shimmer" style={{ width: '250px', height: '40px', background: '#e5e7eb', borderRadius: '8px' }}></div>
      <div className="skeleton-text shimmer" style={{ width: '150px', height: '40px', background: '#e5e7eb', borderRadius: '8px' }}></div>
      <div className="skeleton-text shimmer" style={{ width: '150px', height: '40px', background: '#e5e7eb', borderRadius: '8px' }}></div>
    </div>
    
    {/* Table Header */}
    <div className="skeleton-table-header shimmer" style={{
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
      gap: '1rem',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '8px',
      marginBottom: '0.5rem'
    }}>
      {['Name', 'Email', 'Sector', 'Location', 'Status', 'Actions'].map((header, i) => (
        <div key={i} className="skeleton-text shimmer" style={{ width: '100%', height: '20px', background: '#e5e7eb', borderRadius: '4px' }}></div>
      ))}
    </div>
    
    {/* Table Rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="skeleton-table-row shimmer" style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
        gap: '1rem',
        padding: '1rem',
        background: '#ffffff',
        borderRadius: '8px',
        marginBottom: '0.5rem',
        border: '1px solid #e5e7eb'
      }}>
        {[1, 2, 3, 4, 5, 6].map((j) => (
          <div key={j} className="skeleton-text shimmer" style={{ 
            width: j === 1 ? '80%' : '100%', 
            height: '16px', 
            background: '#e5e7eb',
            borderRadius: '4px'
          }}></div>
        ))}
      </div>
    ))}
  </div>
);

/**
 * Association Form Skeleton
 */
export const AssociationFormSkeleton = () => (
  <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
    {/* Progress Bar Skeleton */}
    <div style={{ marginBottom: '2rem' }}>
      <div className="skeleton-text shimmer" style={{ width: '200px', height: '24px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '1rem' }}></div>
      <div className="skeleton-text shimmer" style={{ width: '100%', height: '12px', background: '#e5e7eb', borderRadius: '6px' }}></div>
    </div>
    
    {/* Form Sections */}
    {[1, 2, 3, 4].map((section) => (
      <div key={section} style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        border: '1px solid #e5e7eb'
      }}>
        <div className="skeleton-text shimmer" style={{ width: '180px', height: '20px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '1rem' }}></div>
        
        <div style={{ display: 'grid', gap: '1rem' }}>
          {[1, 2].map((field) => (
            <div key={field}>
              <div className="skeleton-text shimmer" style={{ width: '120px', height: '16px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '0.5rem' }}></div>
              <div className="skeleton-text shimmer" style={{ width: '100%', height: '40px', background: '#e5e7eb', borderRadius: '8px' }}></div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default AssociationDashboardSkeleton;
